import { useAuthStore } from "#imports";
import { resolvePostAuthDestination } from '~/lib/authDestination';
import { shouldIsolatePortalInvitationSession } from '~/lib/clientPortalInvitation';

export default defineNuxtRouteMiddleware(async (to) => {
    if (import.meta.server) {
        return;
    }

    const authStore = useAuthStore();
    const tenantStore = useTenantStore();
    const portalStore = useClientPortalStore();
    const userStore = useUserStore();
    const auth = useNuxtApp().$auth;
    const accessUnavailableError = () => createError({
        statusCode: 503,
        statusMessage: 'Workspace access could not be loaded. Please refresh and try again.',
    });
    await authStore.awaitAuthReady();
    const user = authStore.user || auth.currentUser;

    if (user) {
        if (to.path === '/auth/client-login') {
            const inviteToken = typeof to.query.inviteToken === 'string' ? to.query.inviteToken.trim() : '';
            let invitedEmail = '';

            if (inviteToken) {
                const invitation = await useClientStore().validatePortalInvitation(inviteToken);
                invitedEmail = String(invitation?.email || '').trim().toLowerCase();
            } else if (to.query.inviteAccount === '1' && typeof to.query.email === 'string') {
                invitedEmail = to.query.email.trim().toLowerCase();
            }

            if (invitedEmail) {
                if (shouldIsolatePortalInvitationSession(invitedEmail, user.email)) {
                    await authStore.logout();
                    portalStore.clearAccess();
                }
                return;
            }
        }

        if (!user.emailVerified) {
            if (to.path !== '/auth/verify-email') {
                const redirect = typeof to.query.redirect === 'string' && to.query.redirect.length > 0
                    ? to.query.redirect
                    : '/onboarding';
                return navigateTo(`/auth/verify-email?redirect=${encodeURIComponent(redirect)}`);
            }
            return;
        }

        if (!userStore.user || userStore.user.id !== user.uid) {
            const profileResolved = await userStore.fetchUser(user.uid);
            if (!profileResolved) {
                throw accessUnavailableError();
            }
        }

        const shouldHydrateTenants = tenantStore.loadedForUserId !== user.uid
            || (!tenantStore.currentTenantId && tenantStore.tenants.length === 0);

        if (shouldHydrateTenants) {
            const membershipsResolved = await tenantStore.fetchUserTenants(user.uid);
            if (!membershipsResolved) {
                throw accessUnavailableError();
            }
        }

        if (typeof to.query.redirect === 'string' && to.query.redirect.length > 0) {
            return navigateTo(to.query.redirect);
        }

        if (to.path === '/auth/client-login' && userStore.hasClientAccess && !userStore.hasTeamAccess) {
            const portalClientSlug = await portalStore.findFirstClientSlug();
            if (portalClientSlug) {
                return navigateTo(`/portal/${portalClientSlug}`);
            }
            if (!userStore.hasTeamAccess) {
                return navigateTo('/portal');
            }
        }

        if (tenantStore.tenants.length > 0 || userStore.hasTeamAccess) {
            return navigateTo(await resolvePostAuthDestination({
                authSource: to.path === '/auth/client-login' ? 'client-login' : 'login',
            }), { external: true });
        }

        if (userStore.hasClientAccess) {
            const portalClientSlug = await portalStore.findFirstClientSlug();
            if (portalClientSlug) {
                return navigateTo(`/portal/${portalClientSlug}`);
            }
            return navigateTo('/portal');
        }

        if (!userStore.hasTeamAccess) {
            if (to.path === '/auth/login' || to.path === '/auth/signup') {
                return navigateTo('/onboarding');
            }
        }
        return navigateTo('/onboarding');
    }
});
